import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface PercentSliderProps {
  id: string;
  label: string;
  /** En % (40, no 0,4). `null` = pista vacía y lectura «—». */
  value: number | null;
  min: number;
  max: number;
  step: number;
  onChange: (pct: number) => void;
  /** Lectura grande y `aria-valuetext`. Por defecto `${pct}%`. */
  formatValue?: (pct: number) => string;
  hint?: ReactNode;
  /** Debajo de la pista (p. ej. una fila de figuras). */
  children?: ReactNode;
  className?: string;
}

/**
 * Deslizador con lectura grande. Un valor fuera de [min, max] o de la rejilla se lee tal cual;
 * el pulgar se pinta en el extremo y el valor sólo cambia cuando el usuario lo toca.
 */
export function PercentSlider({
  id,
  label,
  value,
  min,
  max,
  step,
  onChange,
  formatValue = (pct) => `${pct}%`,
  hint,
  children,
  className,
}: PercentSliderProps) {
  const clamped = value == null ? min : Math.max(min, Math.min(max, value));
  const fill = value == null || max === min ? 0 : ((clamped - min) / (max - min)) * 100;
  const reading = value == null ? '—' : formatValue(value);
  return (
    <div className={cn('flex flex-col gap-2.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-medium text-[var(--color-fg-primary)]">
          {label}
        </label>
        <span className="font-[var(--font-dash-mono)] text-[28px] font-bold leading-none tabular-nums text-[var(--color-fg-primary)]">
          {reading}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={clamped}
        aria-valuetext={reading}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ '--p': `${fill}%` } as CSSProperties}
        className={cn(
          'm-0 h-11 w-full cursor-pointer appearance-none rounded-full bg-transparent focus:outline-none',
          // Pista: relleno hasta el pulgar. Alta de 10 px dentro de un control de 44 px.
          '[&::-webkit-slider-runnable-track]:h-2.5 [&::-webkit-slider-runnable-track]:rounded-full',
          '[&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,var(--color-fg-primary)_0,var(--color-fg-primary)_var(--p),var(--color-border-default)_var(--p),var(--color-border-default)_100%)]',
          '[&::-moz-range-track]:h-2.5 [&::-moz-range-track]:rounded-full',
          '[&::-moz-range-track]:bg-[linear-gradient(to_right,var(--color-fg-primary)_0,var(--color-fg-primary)_var(--p),var(--color-border-default)_var(--p),var(--color-border-default)_100%)]',
          // Pulgar: 30 px de acento con borde de tinta.
          '[&::-webkit-slider-thumb]:-mt-[10px] [&::-webkit-slider-thumb]:h-[30px] [&::-webkit-slider-thumb]:w-[30px] [&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-[var(--color-fg-primary)]',
          '[&::-webkit-slider-thumb]:bg-[var(--color-xk-accent)] [&::-webkit-slider-thumb]:shadow-md',
          '[&::-moz-range-thumb]:h-[26px] [&::-moz-range-thumb]:w-[26px] [&::-moz-range-thumb]:rounded-full',
          '[&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-[var(--color-fg-primary)] [&::-moz-range-thumb]:bg-[var(--color-xk-accent)]',
          'focus-visible:[&::-webkit-slider-thumb]:outline focus-visible:[&::-webkit-slider-thumb]:outline-[3px] focus-visible:[&::-webkit-slider-thumb]:outline-offset-2 focus-visible:[&::-webkit-slider-thumb]:outline-[var(--color-fg-primary)]',
          'focus-visible:[&::-moz-range-thumb]:outline focus-visible:[&::-moz-range-thumb]:outline-[3px] focus-visible:[&::-moz-range-thumb]:outline-offset-2 focus-visible:[&::-moz-range-thumb]:outline-[var(--color-fg-primary)]',
        )}
      />
      {children}
      {hint ? <span className="text-xs text-[var(--color-fg-muted)]">{hint}</span> : null}
    </div>
  );
}
